import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  ArrowLeft, Plus, Edit2, Trash2, ExternalLink, 
  DollarSign, Calendar, RefreshCw, Mail, Globe, Truck, Server
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { value: 'hosting', label: 'Hosting', icon: Server, color: 'text-blue-500' },
  { value: 'email', label: 'Email', icon: Mail, color: 'text-purple-500' },
  { value: 'domain', label: 'Domain', icon: Globe, color: 'text-green-500' },
  { value: 'tracking', label: 'Tracking', icon: Truck, color: 'text-tiffany-500' },
  { value: 'other', label: 'Other', icon: Server, color: 'text-zinc-400' },
];

const BILLING_CYCLES = [
  { value: 'monthly', label: 'Monthly' },
  { value: 'yearly', label: 'Yearly' },
  { value: 'one_time', label: 'One-time' },
];

const getCategoryInfo = (category) => {
  return CATEGORIES.find(c => c.value === category) || CATEGORIES[4];
};

const formatDate = (dateString) => {
  if (!dateString || dateString === 'None') return null;
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return null;
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${month}/${day}/${date.getFullYear()}`;
  } catch {
    return null;
  }
};

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
};

export default function AdminOperatingCosts() {
  const { getAuthHeader } = useAuth();
  const [loading, setLoading] = useState(true);
  const [expenses, setExpenses] = useState([]);
  const [summary, setSummary] = useState({
    monthly_total: 0,
    yearly_total: 0,
    total_services: 0
  });
  
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [formData, setFormData] = useState({
    name: '', category: 'other', amount: '', billing_cycle: 'monthly',
    next_renewal_date: '', is_paid: true, auto_renew: true,
    payment_method: '', notes: '', url: ''
  });

  const fetchExpenses = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/admin/operating-costs`, {
        headers: getAuthHeader()
      });
      setExpenses(response.data.expenses || []);
      setSummary(response.data.summary || {});
    } catch (error) {
      toast.error('Failed to load expenses');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => { fetchExpenses(); }, [fetchExpenses]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = { ...formData, amount: parseFloat(formData.amount) || 0, next_renewal_date: formData.next_renewal_date || null };
      if (editingExpense) {
        await axios.put(`${API}/admin/operating-costs/${editingExpense.id}`, data, { headers: getAuthHeader() });
        toast.success('Updated');
      } else {
        await axios.post(`${API}/admin/operating-costs`, data, { headers: getAuthHeader() });
        toast.success('Added');
      }
      setDialogOpen(false);
      resetForm();
      fetchExpenses();
    } catch { toast.error('Failed to save'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete?')) return;
    try {
      await axios.delete(`${API}/admin/operating-costs/${id}`, { headers: getAuthHeader() });
      toast.success('Deleted');
      fetchExpenses();
    } catch { toast.error('Failed'); }
  };

  const handleMarkPaid = async (id) => {
    try {
      await axios.post(`${API}/admin/operating-costs/${id}/mark-paid`, {}, { headers: getAuthHeader() });
      toast.success('Marked paid');
      fetchExpenses();
    } catch { toast.error('Failed'); }
  };

  const openEditDialog = (expense) => {
    setEditingExpense(expense);
    setFormData({
      name: expense.name || '', category: expense.category || 'other',
      amount: expense.amount?.toString() || '', billing_cycle: expense.billing_cycle || 'monthly',
      next_renewal_date: expense.next_renewal_date ? expense.next_renewal_date.split('T')[0] : '',
      is_paid: expense.is_paid ?? true, auto_renew: expense.auto_renew ?? true,
      payment_method: expense.payment_method || '', notes: expense.notes || '', url: expense.url || ''
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingExpense(null);
    setFormData({ name: '', category: 'other', amount: '', billing_cycle: 'monthly', next_renewal_date: '', is_paid: true, auto_renew: true, payment_method: '', notes: '', url: '' });
  };

  if (loading) {
    return <div className="min-h-screen bg-[#050505] flex items-center justify-center"><div className="text-tiffany-500 font-mono">LOADING...</div></div>;
  }

  return (
    <div className="min-h-screen bg-[#050505]">
      <header className="fixed top-0 left-0 right-0 z-50 border-b border-zinc-800 bg-[#050505]/95 backdrop-blur-sm">
        <div className="max-w-6xl mx-auto px-4 h-12 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/admin" className="text-zinc-400 hover:text-white"><ArrowLeft className="w-5 h-5" /></Link>
            <h1 className="font-mono text-base text-white">COSTS</h1>
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={fetchExpenses} variant="ghost" size="sm" className="text-zinc-400 hover:text-white rounded-none h-8 w-8 p-0">
              <RefreshCw className="w-4 h-4" />
            </Button>
            <Button onClick={() => { resetForm(); setDialogOpen(true); }} size="sm" className="bg-tiffany-500 text-black hover:bg-tiffany-400 rounded-none h-8 px-3">
              <Plus className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      <main className="pt-14 pb-8 px-3 max-w-6xl mx-auto">
        {/* Summary */}
        <div className="flex items-center gap-4 py-3 border-b border-zinc-800 mb-3">
          <div className="flex items-center gap-2">
            <DollarSign className="w-4 h-4 text-green-500" />
            <span className="font-mono text-lg font-bold text-white">{formatCurrency(summary.monthly_total)}</span>
            <span className="font-mono text-xs text-zinc-500">/mo</span>
          </div>
          <div className="flex items-center gap-2 ml-auto">
            <Calendar className="w-4 h-4 text-blue-500" />
            <span className="font-mono text-sm text-zinc-400">{formatCurrency(summary.yearly_total)}/yr</span>
          </div>
        </div>

        {/* Expense List - Clean Cards */}
        <div className="space-y-2">
          {expenses.map((expense) => {
            const categoryInfo = getCategoryInfo(expense.category);
            const CategoryIcon = categoryInfo.icon;
            const renewalDate = formatDate(expense.next_renewal_date);
            
            // Determine status color
            const isPaid = expense.is_paid;
            const statusColor = isPaid ? 'text-green-400 bg-green-500/10' : 'text-yellow-400 bg-yellow-500/10';
            const statusText = isPaid ? 'PAID' : 'DUE';
            
            return (
              <div key={expense.id} className="border border-zinc-800 bg-zinc-900/50">
                {/* Main Card Content */}
                <div className="p-3">
                  {/* Top Row: Icon, Name, Status */}
                  <div className="flex items-center gap-3 mb-3">
                    <div className={`p-2 bg-zinc-800 ${categoryInfo.color}`}>
                      <CategoryIcon className="w-4 h-4" />
                    </div>
                    <span className="font-mono text-base text-white font-bold flex-1 uppercase">{expense.name}</span>
                    <span className={`font-mono text-xs px-2 py-1 ${statusColor}`}>{statusText}</span>
                  </div>
                  
                  {/* Info Grid */}
                  <div className="grid grid-cols-2 gap-x-6 gap-y-2 font-mono text-sm">
                    {/* Cost */}
                    <div className="flex justify-between">
                      <span className="text-zinc-500">COST</span>
                      <span className="text-white font-bold">
                        {expense.amount === 0 ? 'FREE' : formatCurrency(expense.amount)}
                      </span>
                    </div>
                    
                    {/* Billing Cycle */}
                    <div className="flex justify-between">
                      <span className="text-zinc-500">BILLING</span>
                      <span className="text-white">{expense.billing_cycle?.toUpperCase() || '-'}</span>
                    </div>
                    
                    {/* Renewal Date */}
                    <div className="flex justify-between">
                      <span className="text-zinc-500">RENEWS</span>
                      <span className={renewalDate ? 'text-tiffany-400 font-bold' : 'text-zinc-600'}>
                        {renewalDate || 'N/A'}
                      </span>
                    </div>
                    
                    {/* Payment Method */}
                    <div className="flex justify-between">
                      <span className="text-zinc-500">PAYMENT</span>
                      <span className="text-white">{expense.payment_method || '-'}</span>
                    </div>
                  </div>
                  
                  {/* Notes (if any) */}
                  {expense.notes && (
                    <p className="font-mono text-xs text-zinc-500 mt-3 pt-2 border-t border-zinc-800/50">{expense.notes}</p>
                  )}
                  
                  {/* Action Row */}
                  <div className="flex items-center gap-2 mt-3 pt-2 border-t border-zinc-800/50">
                    {expense.url && (
                      <a href={expense.url} target="_blank" rel="noopener noreferrer" className="text-tiffany-500 text-xs flex items-center gap-1 hover:text-tiffany-400">
                        <ExternalLink className="w-3 h-3" /> BILLING PORTAL
                      </a>
                    )}
                    <div className="flex-1" />
                    {!expense.is_paid && (
                      <Button onClick={() => handleMarkPaid(expense.id)} size="sm" className="bg-green-600 text-white rounded-none h-7 px-3 text-xs">
                        MARK PAID
                      </Button>
                    )}
                    <Button onClick={() => openEditDialog(expense)} size="sm" variant="ghost" className="text-zinc-400 hover:text-white h-7 w-7 p-0">
                      <Edit2 className="w-4 h-4" />
                    </Button>
                    <Button onClick={() => handleDelete(expense.id)} size="sm" variant="ghost" className="text-red-400 hover:text-red-300 h-7 w-7 p-0">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="bg-[#0a0a0a] border-zinc-800 rounded-none max-w-md max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-mono text-white">{editingExpense ? 'EDIT' : 'ADD'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-3 mt-4">
              <div>
                <label className="font-mono text-xs text-zinc-500 block mb-1">NAME *</label>
                <Input value={formData.name} onChange={(e) => setFormData(f => ({ ...f, name: e.target.value }))} className="bg-zinc-900 border-zinc-700 text-white rounded-none" required />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="font-mono text-xs text-zinc-500 block mb-1">CATEGORY</label>
                  <Select value={formData.category} onValueChange={(v) => setFormData(f => ({ ...f, category: v }))}>
                    <SelectTrigger className="bg-zinc-900 border-zinc-700 text-white rounded-none"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800 rounded-none">
                      {CATEGORIES.map(c => <SelectItem key={c.value} value={c.value} className="text-white">{c.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="font-mono text-xs text-zinc-500 block mb-1">CYCLE</label>
                  <Select value={formData.billing_cycle} onValueChange={(v) => setFormData(f => ({ ...f, billing_cycle: v }))}>
                    <SelectTrigger className="bg-zinc-900 border-zinc-700 text-white rounded-none"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800 rounded-none">
                      {BILLING_CYCLES.map(c => <SelectItem key={c.value} value={c.value} className="text-white">{c.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="font-mono text-xs text-zinc-500 block mb-1">AMOUNT *</label>
                  <Input type="number" step="0.01" value={formData.amount} onChange={(e) => setFormData(f => ({ ...f, amount: e.target.value }))} className="bg-zinc-900 border-zinc-700 text-white rounded-none" required />
                </div>
                <div>
                  <label className="font-mono text-xs text-zinc-500 block mb-1">RENEWS ON</label>
                  <Input type="date" value={formData.next_renewal_date} onChange={(e) => setFormData(f => ({ ...f, next_renewal_date: e.target.value }))} className="bg-zinc-900 border-zinc-700 text-white rounded-none [color-scheme:dark]" />
                </div>
              </div>
              <div>
                <label className="font-mono text-xs text-zinc-500 block mb-1">PAYMENT METHOD</label>
                <Input value={formData.payment_method} onChange={(e) => setFormData(f => ({ ...f, payment_method: e.target.value }))} className="bg-zinc-900 border-zinc-700 text-white rounded-none" />
              </div>
              <div>
                <label className="font-mono text-xs text-zinc-500 block mb-1">BILLING URL</label>
                <Input value={formData.url} onChange={(e) => setFormData(f => ({ ...f, url: e.target.value }))} className="bg-zinc-900 border-zinc-700 text-white rounded-none" />
              </div>
              <div>
                <label className="font-mono text-xs text-zinc-500 block mb-1">NOTES</label>
                <Input value={formData.notes} onChange={(e) => setFormData(f => ({ ...f, notes: e.target.value }))} className="bg-zinc-900 border-zinc-700 text-white rounded-none" />
              </div>
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={formData.is_paid} onChange={(e) => setFormData(f => ({ ...f, is_paid: e.target.checked }))} className="w-4 h-4" />
                  <span className="font-mono text-xs text-zinc-400">PAID</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={formData.auto_renew} onChange={(e) => setFormData(f => ({ ...f, auto_renew: e.target.checked }))} className="w-4 h-4" />
                  <span className="font-mono text-xs text-zinc-400">AUTO-RENEW</span>
                </label>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" onClick={() => setDialogOpen(false)} variant="outline" className="border-zinc-700 text-zinc-400 rounded-none">CANCEL</Button>
                <Button type="submit" className="bg-tiffany-500 text-black hover:bg-tiffany-400 rounded-none">{editingExpense ? 'UPDATE' : 'ADD'}</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
